import {
  FormDialog,
  InputField,
  TextareaField,
  SelectField,
  NumberInputField,
} from "@/components/ui/form/form-dialog";
import { LocationField } from "@/components/ui/location-field";
import { useConvexForm } from "@/hooks/useFormValidation";
import { useToastMutation } from "@/hooks/useToastMutation";
import { validatorCompany } from "@/convex/companies";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import ImageUpload from "../images/image-upload";
import { createNumberParser } from "@/hooks/parsers/formParsers";
import { phoneFieldParser, COUNTRY_CODES } from "@/hooks/parsers/phoneParser";
import { PhoneState } from "@/hooks/parsers/phoneParser";

// Crear validator para el formulario omitiendo campos automáticos
const validatorForm = validatorCompany.omit("createdAt", "updatedAt");

// Monedas comunes
const CURRENCIES = [
  { value: "HNL", label: "Lempira Hondureño (HNL)" },
  { value: "USD", label: "Dólar Estadounidense (USD)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "MXN", label: "Peso Mexicano (MXN)" },
  { value: "GTQ", label: "Quetzal Guatemalteco (GTQ)" },
  { value: "NIO", label: "Córdoba Nicaragüense (NIO)" },
  { value: "CRC", label: "Colón Costarricense (CRC)" },
  { value: "PAB", label: "Balboa Panameño (PAB)" },
];

// Términos de pago comunes
const PAYMENT_TERMS = [
  { value: "net_15", label: "Neto 15 días" },
  { value: "net_30", label: "Neto 30 días" },
  { value: "net_60", label: "Neto 60 días" },
  { value: "due_on_receipt", label: "Al recibir" },
  { value: "prepayment", label: "Prepago" },
  { value: "custom", label: "Personalizado" },
];

// Estados de empresa
const COMPANY_STATUS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
  { value: "suspended", label: "Suspendido" },
];

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Doc<"companies">;
  onSuccess?: () => void;
}

export default function CompanyModal({
  isOpen,
  onClose,
  company,
  onSuccess,
}: CompanyModalProps) {
  console.log("CompanyModal renderizado:", {
    isOpen,
    company,
    hasCompany: !!company,
    companyId: company?._id,
    companyName: company?.name,
  });
  const { execute: createCompany } = useToastMutation(
    api.companies.mutations.createCompany,
    {
      loadingMessage: "Creando empresa...",
      successMessage: "Empresa creada exitosamente",
      errorMessage: "Error al crear la empresa",
    },
  );

  const { execute: updateCompany } = useToastMutation(
    api.companies.mutations.updateCompany,
    {
      loadingMessage: "Actualizando empresa...",
      successMessage: "Empresa actualizada exitosamente",
      errorMessage: "Error al actualizar la empresa",
    },
  );

  // Estado para manejar el teléfono (código + número)
  const [phoneState, setPhoneState] = useState<PhoneState>({
    countryCode: "+504",
    number: "",
  });

  // Estado para el logo
  const [logoId, setLogoId] = useState<Id<"_storage"> | undefined>(
    company?.logoId,
  );

  // Inicializar estado del teléfono cuando cambia la empresa
  useEffect(() => {
    console.log("CompanyModal useEffect - company changed:", {
      company,
      hasCompany: !!company,
      companyId: company?._id,
      companyName: company?.name,
      phone: company?.phone,
      logoId: company?.logoId,
    });

    if (company?.phone) {
      const parsed = phoneFieldParser.parse(company.phone);
      setPhoneState(parsed);
    } else {
      setPhoneState({ countryCode: "+504", number: "" });
    }
    setLogoId(company?.logoId);
  }, [company]);

  const { handleSubmit, isLoading, formId, useField } = useConvexForm<
    typeof validatorForm.fields,
    any
  >({
    isUpdate: !!company,
    validator: validatorForm.fields,
    mode: "hybrid",
    initialData: company
      ? {
          ...company,
          // El parser manejará el teléfono
        }
      : {
          status: "active",
          defaultCurrency: "HNL",
          defaultTaxRate: 15,
          defaultProfitMargin: 20,
          paymentTerms: "net_30",
        },
    initialDataId: company?._id,
    controlledFields: [
      "phone",
      "status",
      "defaultCurrency",
      "defaultTaxRate",
      "defaultProfitMargin",
      "paymentTerms",
    ],
    parsers: {
      phone: {
        ...phoneFieldParser,
        // Sobrescribir parse para usar el estado local
        parse: () => phoneState,
        // Sobrescribir format para usar el estado local
        format: () => phoneFieldParser.format(phoneState),
      },
      defaultTaxRate: createNumberParser({
        min: 0,
        max: 100,
        defaultValue: 15,
        decimals: 2,
      }),
      defaultProfitMargin: createNumberParser({
        min: 0,
        max: 100,
        defaultValue: 20,
        decimals: 2,
      }),
    },
    onSubmit: async (formData, isUpdate) => {
      console.log("CompanyModal onSubmit:", {
        isUpdate,
        companyId: company?._id,
        formData,
        logoId,
      });

      try {
        // Preparar datos para la mutación, convirtiendo null a undefined
        const cleanData = { ...formData, logoId };

        // Filtrar nulls explícitamente
        const mutationData: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(cleanData)) {
          if (value !== null && value !== undefined) {
            mutationData[key] = value;
          }
        }

        console.log(
          "CompanyModal mutationData (after filtering nulls):",
          mutationData,
        );

        if (isUpdate && company?._id) {
          await updateCompany({
            id: company._id,
            ...(mutationData as typeof formData),
          });
        } else {
          await createCompany(mutationData as typeof formData);
        }
      } catch (error) {
        console.error("Error saving company:", error);
        toast.error("Error al guardar la empresa");
        throw error;
      }
    },
    onSuccess: () => {
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  const handleCloseModal = () => {
    onClose();
  };

  const handleImageChange = (newLogoId: Id<"_storage"> | undefined) => {
    setLogoId(newLogoId);
  };

  // Campos controlados
  const defaultTaxRate = useField(
    "defaultTaxRate",
    company?.defaultTaxRate || 15,
  );
  const defaultProfitMargin = useField(
    "defaultProfitMargin",
    company?.defaultProfitMargin || 20,
  );

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={company ? "Editar Empresa" : "Nueva Empresa"}
      description={
        company
          ? `Modificar información de ${company.name}`
          : "Agrega una nueva empresa a tu base de datos"
      }
      onSubmit={handleSubmit}
      isLoading={isLoading}
      submitLabel={company ? "Actualizar Empresa" : "Crear Empresa"}
      maxWidth="2xl"
      formId={formId}
    >
      <div className={cn("grid gap-6")}>
        {/* Información Básica */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Información Básica</h3>
          <div className="grid lg:grid-cols-2 gap-4">
            <InputField
              label="Nombre de la Empresa *"
              name="name"
              defaultValue={company?.name || ""}
              placeholder="Ej: Empresa ABC S.A."
              required
              icon="Building"
            />

            <InputField
              label="Nombre Legal *"
              name="legalName"
              defaultValue={company?.legalName || ""}
              placeholder="Ej: Empresa ABC Sociedad Anónima"
              required
              icon="FileText"
            />

            <div className="col-span-2">
              <TextareaField
                label="Descripción"
                name="description"
                defaultValue={company?.description || ""}
                placeholder="Somos una empresa de..."
                rows={3}
                icon="AlignLeft"
              />
            </div>

            <InputField
              label="Sitio Web"
              name="website"
              defaultValue={company?.website || ""}
              placeholder="https://empresa.com"
              icon="Globe"
            />

            <InputField
              label="Correo Electrónico"
              name="email"
              type="email"
              defaultValue={company?.email || ""}
              placeholder="contacto@empresa.com"
              icon="Mail"
            />
          </div>
        </div>

        {/* Teléfono con código de país */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Teléfono</h3>
          <div className="space-y-2">
            <label className="text-sm font-medium">Teléfono</label>
            <div className="flex gap-2">
              <SelectField
                label="Código de País"
                name="countryCode"
                value={phoneState.countryCode}
                onValueChange={(value) =>
                  setPhoneState((prev) => ({ ...prev, countryCode: value }))
                }
                options={COUNTRY_CODES.map((cc) => ({
                  value: cc.code,
                  label: `${cc.flag} ${cc.code} (${cc.country})`,
                }))}
                icon="Phone"
                className="w-32"
              />
              <InputField
                label="Número"
                name="phoneNumber"
                value={phoneState.number}
                onChange={(e) =>
                  setPhoneState((prev) => ({ ...prev, number: e.target.value }))
                }
                placeholder="12345678"
                icon="Phone"
                className="flex-1"
              />
            </div>
            {phoneState.number && (
              <p className="text-sm text-muted-foreground">
                Número completo:{" "}
                <span className="font-mono">
                  {phoneState.countryCode}
                  {phoneState.number.replace(/[\s\-\(\)]/g, "")}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Información Fiscal */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Información Fiscal</h3>
          <div className="grid lg:grid-cols-2 gap-4">
            <InputField
              label="RTN o Tax ID"
              name="taxId"
              defaultValue={company?.taxId || ""}
              placeholder="Número de identificación fiscal"
              icon="FileDigit"
            />
          </div>
        </div>

        {/* Configuración Empresarial */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Configuración Empresarial</h3>
          <div className="grid lg:grid-cols-2 gap-4">
            <SelectField
              label="Moneda por Defecto"
              name="defaultCurrency"
              defaultValue={company?.defaultCurrency || "HNL"}
              options={CURRENCIES}
              icon="DollarSign"
            />

            <NumberInputField
              label="Tasa de Impuesto por Defecto (%)"
              name="defaultTaxRate"
              min={0}
              max={100}
              step={0.01}
              placeholder="Ej: 15.00"
              icon="Percent"
              {...defaultTaxRate}
            />

            <NumberInputField
              label="Margen de Ganancia por Defecto (%)"
              name="defaultProfitMargin"
              min={0}
              max={100}
              step={0.01}
              placeholder="Ej: 20.00"
              icon="TrendingUp"
              {...defaultProfitMargin}
            />

            <SelectField
              label="Términos de Pago por Defecto"
              name="paymentTerms"
              defaultValue={company?.paymentTerms || "net_30"}
              options={PAYMENT_TERMS}
              icon="Calendar"
            />
          </div>
        </div>

        {/* Ubicación */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Ubicación</h3>
          <LocationField
            key={company?._id}
            defaultCountry={company?.country}
            defaultCity={company?.city}
            defaultState={company?.state}
            defaultAddress={company?.address}
          />
        </div>

        {/* Estado */}
        <div className="space-y-4">
          <h3 className="text-md font-medium">Estado</h3>
          <div className="grid lg:grid-cols-2 gap-4">
            <SelectField
              label="Estado de la Empresa"
              name="status"
              defaultValue={company?.status || "active"}
              options={COMPANY_STATUS}
              icon="Activity"
            />
          </div>
        </div>

        {/* Logo (solo para edición) */}
        {company && (
          <div className="space-y-4">
            <h3 className="text-md font-medium">Logo</h3>
            <div className="space-y-2">
              <ImageUpload
                entityType="company"
                entityId={company._id}
                currentImageId={logoId}
                disabled={isLoading}
                maxWidth={1000}
                maxHeight={1000}
                maxSizeKB={500}
                aspectRatio={1}
                quality={0.8}
                uploadText="Subir Logo"
                dragText="Arrastra y suelta el archivo"
                selectText="Selecciona el archivo"
                onImageChange={handleImageChange}
              />
            </div>
          </div>
        )}
      </div>
    </FormDialog>
  );
}
